import { useState, useEffect } from "react";

import axios from "axios";
import { UserPlus, Trash, Shield, User, EnvelopeSimple, Check, PencilSimple, Key } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Separator } from "../ui/separator";

export function UserManagementDialog({ open, onOpenChange, sessionId, API }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [creating, setCreating] = useState(false);
  const [editingEmail, setEditingEmail] = useState(null);
  const [emailInput, setEmailInput] = useState("");
  const [editingPassword, setEditingPassword] = useState(null);
  const [passwordInput, setPasswordInput] = useState("");

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/users?session_id=${sessionId}`);
      setUsers(response.data);
    } catch (err) {
      
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchUsers();
      setShowAddForm(false);
      setNewUsername("");
      setNewPassword("");
    }
  }, [open]);

  const handleCreateUser = async () => {
    if (!newUsername || !newPassword) {
      
      return;
    }

    try {
      setCreating(true);
      await axios.post(`${API}/admin/users?session_id=${sessionId}`, {
        username: newUsername,
        password: newPassword,
      });
      
      setNewUsername("");
      setNewPassword("");
      setShowAddForm(false);
      fetchUsers();
    } catch (err) {
      
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async (username) => {
    if (!window.confirm(`Möchten Sie den Benutzer '${username}' wirklich löschen?`)) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/users/${username}?session_id=${sessionId}`);
      fetchUsers();
    } catch (err) {}
  };

  const handleSaveEmail = async (username) => {
    try {
      await axios.put(`${API}/admin/users/${username}/email?session_id=${sessionId}`, {
        email: emailInput
      });
      setEditingEmail(null);
      fetchUsers();
    } catch (err) {
      console.error("Email save error:", err);
    }
  };

  const handleSavePassword = async (username) => {
    if (!passwordInput || passwordInput.length < 4) return;
    try {
      await axios.put(`${API}/admin/password?session_id=${sessionId}`, {
        target_username: username,
        new_password: passwordInput
      });
      setEditingPassword(null);
      setPasswordInput("");
    } catch (err) {
      console.error("Password save error:", err);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold uppercase tracking-tight">
            Benutzerverwaltung
          </DialogTitle>
          <DialogDescription>
            Benutzer hinzufügen oder entfernen. Nur User-Rolle kann erstellt werden.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-4">
          {/* User list */}
          <div className="space-y-2">
            <Label className="uppercase tracking-[0.15em] text-xs font-semibold">
              Aktive Benutzer
            </Label>
            
            {loading ? (
              <div className="text-center py-4 text-muted-foreground">
                Lade Benutzer...
              </div>
            ) : (
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {users.map((user) => (
                  <div
                    key={user.username}
                    className="flex items-center justify-between p-3 bg-secondary rounded"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {user.role === "admin" ? (
                        <Shield className="h-5 w-5 text-primary flex-shrink-0" weight="fill" />
                      ) : (
                        <User className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{user.username}</p>
                        <p className="text-xs text-muted-foreground uppercase">
                          {user.role === "admin" ? "Administrator" : "Benutzer"}
                        </p>
                        <div className="flex items-center gap-1 mt-1">
                          <EnvelopeSimple className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                          {editingEmail === user.username ? (
                            <div className="flex items-center gap-1">
                              <input
                                data-testid={`email-input-${user.username}`}
                                type="email"
                                value={emailInput}
                                onChange={(e) => setEmailInput(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleSaveEmail(user.username)}
                                className="h-5 px-1.5 text-xs bg-background border border-border rounded w-40 focus:outline-none focus:ring-1 focus:ring-primary"
                                placeholder="E-Mail eingeben"
                                autoFocus
                              />
                              <button
                                data-testid={`email-save-${user.username}`}
                                onClick={() => handleSaveEmail(user.username)}
                                className="p-0.5 rounded hover:bg-primary/10 text-primary transition-colors"
                                title="Speichern"
                              >
                                <Check className="h-3 w-3" weight="bold" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-muted-foreground truncate">
                                {user.email || "Keine E-Mail"}
                              </span>
                              <button
                                data-testid={`email-edit-${user.username}`}
                                onClick={() => { setEditingEmail(user.username); setEmailInput(user.email || ""); }}
                                className="p-0.5 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                                title="E-Mail bearbeiten"
                              >
                                <PencilSimple className="h-3 w-3" />
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1 mt-0.5">
                          <Key className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                          {editingPassword === user.username ? (
                            <div className="flex items-center gap-1">
                              <input
                                data-testid={`password-input-${user.username}`}
                                type="password"
                                value={passwordInput}
                                onChange={(e) => setPasswordInput(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleSavePassword(user.username)}
                                className="h-5 px-1.5 text-xs bg-background border border-border rounded w-40 focus:outline-none focus:ring-1 focus:ring-primary"
                                placeholder="Neues Passwort (min. 4)"
                                autoFocus
                              />
                              <button
                                data-testid={`password-save-${user.username}`}
                                onClick={() => handleSavePassword(user.username)}
                                className="p-0.5 rounded hover:bg-primary/10 text-primary transition-colors"
                                title="Passwort speichern"
                              >
                                <Check className="h-3 w-3" weight="bold" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-muted-foreground">********</span>
                              <button
                                data-testid={`password-edit-${user.username}`}
                                onClick={() => { setEditingPassword(user.username); setPasswordInput(""); }}
                                className="p-0.5 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                                title="Passwort ändern"
                              >
                                <PencilSimple className="h-3 w-3" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {user.role !== "admin" && (
                      <button
                        onClick={() => handleDeleteUser(user.username)}
                        className="p-2 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors flex-shrink-0"
                        title="Benutzer löschen"
                        data-testid={`delete-user-${user.username}`}
                      >
                        <Trash className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <Separator />

          {/* Add user form */}
          {!showAddForm ? (
            <Button
              variant="outline"
              onClick={() => setShowAddForm(true)}
              className="w-full"
              data-testid="show-add-user-form"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Neuen Benutzer hinzufügen
            </Button>
          ) : (
            <div className="space-y-4 p-4 border border-border rounded bg-card">
              <div className="space-y-2">
                <Label className="uppercase tracking-[0.15em] text-xs font-semibold">
                  Benutzername
                </Label>
                <Input
                  data-testid="new-username-input"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  placeholder="Mindestens 3 Zeichen"
                  className="input-focus"
                />
              </div>

              <div className="space-y-2">
                <Label className="uppercase tracking-[0.15em] text-xs font-semibold">
                  Passwort
                </Label>
                <Input
                  data-testid="new-user-password-input"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Mindestens 4 Zeichen"
                  className="input-focus"
                />
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAddForm(false);
                    setNewUsername("");
                    setNewPassword("");
                  }}
                  className="flex-1"
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={handleCreateUser}
                  disabled={creating || !newUsername || !newPassword}
                  className="flex-1"
                  data-testid="create-user-button"
                >
                  {creating ? "Erstellen..." : "Erstellen"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
